import { Chart, ChartEditor, Table, useDashboard, useDashboardReport, useExport, useQuill } from "@quillsql/react"
import { useState } from "react"

import { AccessCheck } from "@particles"

import { Button } from "@atoms"

import { Dialog } from "@molecules"
import DialogHeader from "@molecules/Dialog/DialogHeader/DialogHeader"

import { EDITABLE_SECTION_NAME } from "@pages/Reports/ReportDashboard/ReportDashboard.constants"
import ReportEditorDialog from "@pages/Reports/ReportDashboard/ReportEditorDialog/ReportEditorDialog"
import { QuillButton, QuillDeleteButton } from "@pages/Reports/ReportDashboard/components/QuillButton/QuillButton"
import QuillDateRangePicker from "@pages/Reports/ReportDashboard/components/QuillDateRangePicker/QuillDateRangePicker"
import QuillDialog from "@pages/Reports/ReportDashboard/components/QuillDialog/QuillDialog"
import QuillFilterContainer from "@pages/Reports/ReportDashboard/components/QuillFilterContainer/QuillFilterContainer"
import QuillMultiSelect from "@pages/Reports/ReportDashboard/components/QuillMultiSelect/QuillMultiSelect"
import QuillSelect from "@pages/Reports/ReportDashboard/components/QuillSelect/QuillSelect"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./ReportDetailDialog.module.scss"
import { ReportDetailDialogProps } from "./ReportDetailDialog.types"

export default function ReportDetailDialog(props: ReportDetailDialogProps) {
    const { isOpen, onOpenChange, reportId, dashboardId } = props

    const { data, loading, error } = useQuill(reportId)
    const { sections, isLoading: sectionsLoading } = useDashboard(dashboardId)
    const { deleteReport } = useDashboardReport(reportId)
    const { downloadCSV, isCSVLoading } = useExport(reportId)
    const [isEditChartDialogOpen, setIsEditChartDialogOpen] = useState(false)
    const [isEditQueryDialogOpen, setIsEditQueryDialogOpen] = useState(false)

    const reportIsEditable = sections?.[EDITABLE_SECTION_NAME]?.some((report) => report.id === reportId) ?? false

    const handleEditChart = () => {
        if (reportIsEditable) {
            setIsEditChartDialogOpen(true)
        }
    }

    const handleEditQuery = () => {
        if (reportIsEditable) {
            setIsEditQueryDialogOpen(true)
        }
    }

    const handleDeleteReport = () => {
        if (reportIsEditable) {
            deleteReport()
            onOpenChange(false)
        }
    }

    const handleDownloadCSV = () => {
        void downloadCSV()
    }

    if (loading || sectionsLoading) {
        return (
            <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={onOpenChange}>
                <Dialog.Content>
                    <DialogHeader.Root>
                        <DialogHeader.Title title="Loading..." />
                        <DialogHeader.CloseButton />
                    </DialogHeader.Root>
                    <Dialog.Body>
                        <div className={styles.loading}>Loading report...</div>
                    </Dialog.Body>
                </Dialog.Content>
            </Dialog.Root>
        )
    }

    if (error) {
        return (
            <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={onOpenChange}>
                <Dialog.Content>
                    <DialogHeader.Root>
                        <DialogHeader.Title title="Error" />
                        <DialogHeader.CloseButton />
                    </DialogHeader.Root>
                    <Dialog.Body>
                        <div className={styles.error}>Error loading report</div>
                    </Dialog.Body>
                </Dialog.Content>
            </Dialog.Root>
        )
    }

    return (
        <>
            <QuillDialog title={data?.name ?? "Report"} isOpen={isOpen} setIsOpen={onOpenChange}>
                <div className={styles.container}>
                    <div className={styles.actions}>
                        {reportIsEditable && (
                            <AccessCheck
                                permissions={{
                                    reports_edit_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <Button variant="subtle" size="md" onClick={handleEditChart}>
                                    Edit Chart
                                </Button>
                                <Button variant="subtle" size="md" onClick={handleEditQuery}>
                                    Edit Query
                                </Button>
                            </AccessCheck>
                        )}
                        <Button variant="subtle" size="md" onClick={handleDownloadCSV} isLoading={isCSVLoading}>
                            Download CSV
                        </Button>
                        {reportIsEditable && (
                            <AccessCheck
                                permissions={{
                                    reports_edit_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <Button variant="danger" size="md" onClick={handleDeleteReport}>
                                    Delete Report
                                </Button>
                            </AccessCheck>
                        )}
                    </div>
                    <div className={styles.chart}>
                        <Chart
                            reportId={reportId}
                            SelectComponent={QuillSelect}
                            MultiSelectComponent={QuillMultiSelect}
                            DateRangePickerComponent={QuillDateRangePicker}
                            FilterContainerComponent={QuillFilterContainer}
                        />
                    </div>
                    {data?.chartType !== "table" && data?.chartType !== "US map" && (
                        <div className={styles.table}>
                            <Table
                                reportId={reportId}
                                SelectComponent={QuillSelect}
                                MultiSelectComponent={QuillMultiSelect}
                                DateRangePickerComponent={QuillDateRangePicker}
                            />
                        </div>
                    )}
                </div>
            </QuillDialog>
            <ReportEditorDialog
                isOpen={isEditQueryDialogOpen}
                onOpenChange={setIsEditQueryDialogOpen}
                dashboardId={dashboardId}
                reportId={reportId}
            />
            <ChartEditor
                isOpen={isEditChartDialogOpen}
                setIsOpen={setIsEditChartDialogOpen}
                reportId={reportId}
                destinationDashboard={dashboardId}
                destinationSection={EDITABLE_SECTION_NAME}
                showTableFormatOptions={true}
                showDashboardFilterFields={true}
                hideDeleteButton={true}
                ButtonComponent={QuillButton}
                SecondaryButtonComponent={QuillButton}
                DeleteButtonComponent={QuillDeleteButton}
                ModalComponent={QuillDialog}
            />
        </>
    )
}
